import { AxiosError } from "axios";

/**
 * Custom API Error Types
 */

export interface APIError {
  message: string;
  code?: string;
  status?: number;
  errors?: Record<string, string[]>; // For validation errors
}

export class APIErrorHandler {
  /**
   * Extract error message from different error formats
   */
  static getErrorMessage(error: unknown): string {
    if (error instanceof AxiosError) {
      // API returned an error response
      if (error.response?.data) {
        const data = error.response.data as any;

        // Check for common error message fields
        const message =
          data.message || data.error || data.detail || data.msg || null;

        if (message && typeof message === "string" && message.trim()) {
          return message;
        }

        // If data is a string, return it
        if (typeof data === "string" && data.trim()) {
          return data;
        }
      }

      // Network error or no response
      if (error.request && !error.response) {
        return "Network error. Please check your internet connection and ensure the API server is running.";
      }

      // Check for specific error codes
      if (error.code === "ERR_NETWORK") {
        return "Unable to connect to the API. Please check if the server is running.";
      }

      if (error.code === "ECONNABORTED") {
        return "Request timeout. Please try again.";
      }

      // Use error message if available
      if (error.message && error.message.trim()) {
        return error.message;
      }

      // Fallback with status code if available
      if (error.response?.status) {
        return `Request failed with status ${error.response.status}`;
      }

      return "An unexpected error occurred while processing your request";
    }

    // Non-Axios error
    if (error instanceof Error) {
      return error.message || "An error occurred";
    }

    // Unknown error type
    if (typeof error === "string") {
      return error;
    }

    return "An unexpected error occurred";
  }

  /**
   * Extract full error object with all details
   */
  static getErrorObject(error: unknown): APIError {
    if (error instanceof AxiosError) {
      const status = error.response?.status;
      const data = error.response?.data as any;

      return {
        message: this.getErrorMessage(error),
        code: data?.code || error.code,
        status,
        errors: data?.errors || undefined,
      };
    }

    return {
      message: this.getErrorMessage(error),
    };
  }

  /**
   * Check if error is a specific HTTP status
   */
  static isStatus(error: unknown, status: number): boolean {
    if (error instanceof AxiosError) {
      return error.response?.status === status;
    }
    return false;
  }

  /**
   * Check if error is a network error
   */
  static isNetworkError(error: unknown): boolean {
    if (error instanceof AxiosError) {
      return Boolean(error.request && !error.response);
    }
    return false;
  }

  /**
   * Check if error is a timeout
   */
  static isTimeout(error: unknown): boolean {
    if (error instanceof AxiosError) {
      return error.code === "ECONNABORTED" || error.message.includes("timeout");
    }
    return false;
  }

  /**
   * Check if error is a validation error
   */
  static isValidationError(error: unknown): boolean {
    return this.isStatus(error, 422);
  }

  /**
   * Check if error is unauthorized
   */
  static isUnauthorized(error: unknown): boolean {
    return this.isStatus(error, 401);
  }

  /**
   * Check if error is forbidden
   */
  static isForbidden(error: unknown): boolean {
    return this.isStatus(error, 403);
  }

  /**
   * Check if error is not found
   */
  static isNotFound(error: unknown): boolean {
    return this.isStatus(error, 404);
  }

  /**
   * Check if error is server error
   */
  static isServerError(error: unknown): boolean {
    if (error instanceof AxiosError) {
      const status = error.response?.status;
      return Boolean(status && status >= 500);
    }
    return false;
  }

  /**
   * Get validation errors as object
   */
  static getValidationErrors(error: unknown): Record<string, string[]> | null {
    if (error instanceof AxiosError && error.response?.data) {
      const data = error.response.data as any;
      return data.errors || null;
    }
    return null;
  }

  /**
   * Format validation errors for display
   */
  static formatValidationErrors(error: unknown): string[] {
    const validationErrors = this.getValidationErrors(error);

    if (!validationErrors) {
      return [];
    }

    return Object.entries(validationErrors).flatMap(([field, messages]) =>
      messages.map((msg) => `${field}: ${msg}`),
    );
  }
}

/**
 * User-friendly error messages for common scenarios
 */
export const ERROR_MESSAGES = {
  NETWORK:
    "Unable to connect to the server. Please check your internet connection.",
  TIMEOUT: "Request timed out. Please try again.",
  UNAUTHORIZED: "Your session has expired. Please log in again.",
  FORBIDDEN: "You do not have permission to perform this action.",
  NOT_FOUND: "The requested resource was not found.",
  SERVER_ERROR: "A server error occurred. Please try again later.",
  VALIDATION: "Please check your input and try again.",
  UNKNOWN: "An unexpected error occurred. Please try again.",
};

export default APIErrorHandler;
